import * as React from "react"

import { cn } from "@/lib/utils"
import { setCustomElementMetadata } from "@/lib/customElements"

const Table = React.forwardRef<
  HTMLTableElement,
  React.HTMLAttributes<HTMLTableElement>
>(({ className, ...props }, ref) => (
  <div className="relative w-full overflow-auto">
    <table
      ref={ref}
      className={cn("w-full caption-bottom text-sm", className)}
      {...props}
    />
  </div>
))
Table.displayName = "Table"

const TableHeader = React.forwardRef<
  HTMLTableSectionElement,
  React.HTMLAttributes<HTMLTableSectionElement>
>(({ className, ...props }, ref) => (
  <thead ref={ref} className={cn("[&_tr]:border-b", className)} {...props} />
))
TableHeader.displayName = "TableHeader"

const TableBody = React.forwardRef<
  HTMLTableSectionElement,
  React.HTMLAttributes<HTMLTableSectionElement>
>(({ className, ...props }, ref) => (
  <tbody
    ref={ref}
    className={cn("[&_tr:last-child]:border-0", className)}
    {...props}
  />
))
TableBody.displayName = "TableBody"

const TableFooter = React.forwardRef<
  HTMLTableSectionElement,
  React.HTMLAttributes<HTMLTableSectionElement>
>(({ className, ...props }, ref) => (
  <tfoot
    ref={ref}
    className={cn(
      "border-t bg-muted/50 font-medium [&>tr]:last:border-b-0",
      className
    )}
    {...props}
  />
))
TableFooter.displayName = "TableFooter"

const TableRow = React.forwardRef<
  HTMLTableRowElement,
  React.HTMLAttributes<HTMLTableRowElement>
>(({ className, ...props }, ref) => (
  <tr
    ref={ref}
    className={cn(
      "border-b transition-colors hover:bg-muted/50 data-[state=selected]:bg-muted",
      className
    )}
    {...props}
  />
))
TableRow.displayName = "TableRow"

const TableHead = React.forwardRef<
  HTMLTableCellElement,
  React.ThHTMLAttributes<HTMLTableCellElement>
>(({ className, ...props }, ref) => (
  <th
    ref={ref}
    className={cn(
      "h-12 px-4 text-left align-middle font-medium text-muted-foreground [&:has([role=checkbox])]:pr-0",
      className
    )}
    {...props}
  />
))
TableHead.displayName = "TableHead"

const TableCell = React.forwardRef<
  HTMLTableCellElement,
  React.TdHTMLAttributes<HTMLTableCellElement>
>(({ className, ...props }, ref) => (
  <td
    ref={ref}
    className={cn("p-4 align-middle [&:has([role=checkbox])]:pr-0", className)}
    {...props}
  />
))
TableCell.displayName = "TableCell"

const TableCaption = React.forwardRef<
  HTMLTableCaptionElement,
  React.HTMLAttributes<HTMLTableCaptionElement>
>(({ className, ...props }, ref) => (
  <caption
    ref={ref}
    className={cn("mt-4 text-sm text-muted-foreground", className)}
    {...props}
  />
))
TableCaption.displayName = "TableCaption"

const TableWebComponentWrapper: React.FC<React.HTMLAttributes<HTMLDivElement> & { _domElement?: HTMLElement }> = (props) => {
  const parseTableContent = (element: HTMLElement | null) => {
    if (!element) return [];
    
    const tableElement = element.querySelector('table');
    if (!tableElement) return [];
    const children: React.ReactNode[] = [];
    
    // Parse thead
    const thead = tableElement.querySelector('thead');
    if (thead) {
      children.push(
        <TableHeader key="header">
          {Array.from(thead.querySelectorAll('tr')).map((tr, trIndex) => (
            <TableRow key={`header-row-${trIndex}`}>
              {Array.from(tr.children).map((cell, cellIndex) => (
                <TableHead key={`header-cell-${cellIndex}`}>{cell.textContent}</TableHead>
              ))}
            </TableRow>
          ))}
        </TableHeader>
      );
    }

    // Parse tbody
    const tbody = tableElement.querySelector('tbody');
    if (tbody) {
      children.push(
        <TableBody key="body">
          {Array.from(tbody.querySelectorAll('tr')).map((tr, trIndex) => (
            <TableRow key={`body-row-${trIndex}`}>
              {Array.from(tr.children).map((cell, cellIndex) => (
                <TableCell key={`body-cell-${cellIndex}`}>{cell.textContent}</TableCell>
              ))}
            </TableRow>
          ))}
        </TableBody>
      );
    }

    // Parse tfoot
    const tfoot = tableElement.querySelector('tfoot');
    if (tfoot) {
      children.push(
        <TableFooter key="footer">
          {Array.from(tfoot.querySelectorAll('tr')).map((tr, trIndex) => (
            <TableRow key={`footer-row-${trIndex}`}>
              {Array.from(tr.children).map((cell, cellIndex) => (
                <TableCell key={`footer-cell-${cellIndex}`}>{cell.textContent}</TableCell>
              ))}
            </TableRow>
          ))}
        </TableFooter>
      );
    }

    return children;
  };

  return (
    <Table>
      {parseTableContent(props._domElement || null)}
    </Table>
  );
};

setCustomElementMetadata({
  component: TableWebComponentWrapper,
  tagName: "raes-table"
});

export {
  Table,
  TableHeader,
  TableBody,
  TableFooter,
  TableHead,
  TableRow,
  TableCell,
  TableCaption,
  TableWebComponentWrapper
}
